/**
 * EAA-1776 (B5 slice 1): /console API client — the portal-api admin surface the
 * console screens compose (registry CRUD, role map, accounts, audit).
 *
 * Same conventions as `orgAdmin.ts`: plain fetch, standard envelope
 * `{success, code, data}` unwrap, the shared typed 401 so callers re-run
 * `login()` instead of rendering a failure. Every endpoint here is re-checked
 * server-side — these calls only shape what the UI shows, never what is
 * allowed.
 *
 * EAA-1817: that re-check is a FINE-GRAINED permission (`@require_permissions` /
 * `@require_permissions_for_method` with a `Permission.*` value), not the
 * `IsSuperAdmin` this line used to claim. The claim was repeated across roughly
 * a dozen comments in this file and under `src/pages/Console/`; it was wrong
 * everywhere. The decorators in `portal_api/directory/*_views.py` are the source
 * of truth, so prefer them over any doc that still quotes the old wording.
 */

import { OrgAdminApiError, OrgAdminUnauthorizedError } from '@/api/orgAdmin';
import { authFetch } from './authFetch';
import { useSessionStore } from '@/store/sessionStore';

// EAA-1817: named so the read shape (`RegistryApp`) and the write shape
// (`AdminAppWritableFields`) cannot drift apart — both are the same server
// choice field.
export type AppType = 'web' | 'api' | 'hybrid';
export type OrgScopeMode = 'selected_orgs' | 'all_orgs';

export interface RegistryApp {
    code: string;
    name: string;
    web_url: string;
    api_base: string;
    audience: string;
    enabled: boolean;
    // EAA-1817: fields `AppRegistrySerializer` actually returns that this
    // interface previously omitted — confirmed field-for-field against
    // `AppRegistrySerializer` in portal-api's `directory/org_serializers.py`.
    // Nullability was checked against the `Application` model, not guessed:
    // every field below is a plain CharField/TextField/JSONField/choice field
    // with `blank=True, default=...`, so it is NEVER server-side null — EXCEPT
    // the three `_at` timestamps, which are `DateTimeField(null=True)`.
    description: string;
    icon_url: string;
    app_type: AppType;
    owner_name: string;
    owner_email: string;
    redirect_uris: string[];
    post_logout_redirect_uris: string[];
    org_scope_mode: OrgScopeMode;
    /**
     * EAA-1709: the LOGIN client (`<code>`) — public + PKCE since EAA-1709, so a
     * web/hybrid app has no login secret any more and `kc_secret_rotated_at`
     * stops moving for it. Empty for an `api` app, which has no login client.
     */
    kc_client_id: string;
    kc_client_created_at: string | null;
    kc_secret_rotated_at: string | null;
    /**
     * EAA-1709: the app BACKEND's machine client (`<code>-api` — confidential,
     * service account, holds `directory:write` on portal-api so the app can push
     * users). `""` = none; only `api`/`hybrid` apps get one. Read-only: the
     * server creates it on create/update, and `POST …/client` rotates its secret.
     */
    kc_api_client_id: string;
    kc_api_secret_rotated_at: string | null;
    catalog_last_synced_at: string | null;
    catalog_last_sync_status: 'never' | 'ok' | 'failed';
    /**
     * EAA-1841: opt-in, server default `false`. True only for apps that
     * implement the role-management receiver contract (ACH is the first —
     * `POST /api/v1/management/roles`), which is what makes `createAppRole`
     * below callable for them. Fail-closed on purpose: the console must offer
     * the create action ONLY where it will actually work, because an app
     * without the flag answers the call with a validation error rather than a
     * 404 the UI could interpret as "not supported yet".
     */
    supports_role_management: boolean;
    /**
     * EAA-1878: same fail-closed shape as `supports_role_management` above,
     * for the org-sync receiver contract (`POST
     * /api/v1/management/organizations`) instead of the role one. ACH is the
     * counter-example this time: its Organization<->Tenant linkage already
     * exists through a different, pre-existing mechanism, so it never
     * implements this endpoint and the console must not offer "Đồng bộ tổ
     * chức" for it. Optional (unlike `supports_role_management`) because this
     * field is landing on the backend concurrently — a response served
     * before that deploy, or a stale cached one, simply omits it, and a
     * missing capability must read as "not supported" (falsy), not throw.
     */
    supports_org_sync?: boolean;
    /**
     * EAA-1884: true only for apps that share this same portal-api deployment
     * rather than being a genuinely external system — ACH is the (only)
     * example today. None of the three sync actions ("Đồng bộ danh mục",
     * "Đồng bộ người dùng", "Đồng bộ tổ chức") make sense for an internal app:
     * they exist to reconcile a SEPARATE system's roles/users/orgs against
     * this registry, and an app that IS this deployment has nothing separate
     * to reconcile against — at best the call is a no-op the UI can't explain,
     * at worst (a naive org-sync) it risks writing back over this same
     * database's real data. Optional for the same reason as
     * `supports_org_sync` above: the field lands on the backend concurrently,
     * so an older or cached response simply omits it, and a missing flag must
     * read as "not internal" (falsy), not throw.
     */
    is_internal?: boolean;
    /**
     * EAA-1817/EAA-1810 (G1): `AppRegistrySerializer` already returns these
     * four fields (confirmed against `org_serializers.py`) — this interface
     * previously omitted them, which is why the old SRS's "no suspend/retire
     * endpoint" gap note was stale: `enabled` is a materialized projection of
     * `lifecycle` (a DB `CheckConstraint` enforces the two never drift), and
     * `POST /admin/apps/{code}/suspend|resume` + `DELETE /admin/apps/{code}`
     * (retire) already exist server-side — see `suspendApp`/`resumeApp`/
     * `retireApp` below. `suspended_at`/`retired_at` are `DateTimeField(null=True)`;
     * `suspended_reason` is a plain `TextField(blank=True, default="")`, never
     * null.
     */
    lifecycle: 'pending' | 'active' | 'suspended' | 'retired';
    suspended_reason: string;
    suspended_at: string | null;
    retired_at: string | null;
    /**
     * EAA-1709: per-app path overrides for the connection check and the catalog
     * sync, relative to `api_base`. A missing key means the server default (see
     * `ENDPOINT_PATH_DEFAULTS`). Optional so a server without migration `0036`
     * still type-checks as "no overrides".
     */
    endpoint_paths?: EndpointPaths;
}

export type EndpointPathKey =
    | 'roles'
    | 'me_access'
    | 'machine_tenants'
    | 'machine_memberships'
    | 'directory_users'
    | 'organizations';
export type EndpointPaths = Partial<Record<EndpointPathKey, string>>;

// EAA-1817: KNOWN GAP — `accepts_multiple_roles` exists on the portal-api
// `Application` MODEL but is NOT one of `AppRegistrySerializer`'s fields, so the
// FE has no way to read it. A UI that wants to warn "this app takes one role
// only" has no data source for that today — this is a backend gap, not
// something missed on this side; do not add a fake client-side derivation.

/**
 * EAA-1817: `GET /api/v1/admin/apps` additionally injects two fields NOT on
 * `AppRegistrySerializer` — `ApplicationService.registry_aggregates()`'s
 * per-row aggregation (see `console_views.py`). Modeled as a separate type so
 * `GET /apps` (launcher) callers of `RegistryApp` aren't forced to carry them.
 */
/**
 * EAA-1810: the HMAC credential an app signs SERVER-TO-SERVER calls with
 * (`Application.external_system`), as the console list reports it. Never
 * secret material — the `shared_secret` exists only in the one-time
 * `provisionAppExternalSystem` response below.
 */
export interface AppExternalSystemLink {
    /** What the app sends as `X-External-System-Id`. */
    system_code: string;
    name: string;
    is_active: boolean;
    secret_rotated_at: string | null;
    /**
     * While non-null and in the future, BOTH the current and the previous
     * secret still verify — that overlap is what stops a rotation from cutting
     * off a live caller. `confirmAppSecretRotation` ends it early.
     */
    previous_secret_expires_at: string | null;
}

export interface AdminRegistryApp extends RegistryApp {
    role_link_count: number;
    catalog_role_count: number;
    /**
     * EAA-1810: `null` when the app has no HMAC credential yet — an explicit
     * null, not an absent key, so "not provisioned" and "not loaded" stay
     * distinguishable. Emitted by the CONSOLE routes only; the launcher's own
     * `GET /apps` deliberately never carries it.
     */
    external_system: AppExternalSystemLink | null;
}

export interface AppRoleOption {
    /** EAA-1817: needed to address `GET/PATCH /admin/app-roles/{id}` — `key`
     *  alone is not unique across organizations. */
    id: string;
    key: string;
    name: string;
    permissions: string[];
    is_active: boolean;
    last_synced_at: string | null;
}

export interface RoleMapRow {
    id: string;
    role_key: string;
    application: string;
    app_role: string | null;
    /**
     * EAA-1817: the app role's DISPLAY name, additive beside `app_role` (the
     * key). `null` only when the catalog role was deleted outright — the server
     * falls back to the key for a role that merely has no name, so this is never
     * an empty string.
     *
     * The console labels chips by NAME and keeps the key in a tooltip (design
     * 2026-08-24). Do not derive this client-side from
     * `GET /admin/app-roles?application=…`: that endpoint is per-application and
     * gated on `APPS_VIEW` — a DIFFERENT permission from the `ROLEMAP_VIEW` a
     * role-map reader holds — so a list spanning several apps would need one
     * request per app, each of which a legitimate reader of this list may not be
     * allowed to make.
     */
    app_role_name: string | null;
    app_role_active: boolean;
    organization_id: string | null;
    is_active: boolean;
    /**
     * EAA-1837: pure ADDITIVE field — `RoleMapService.is_stale()` is the server's
     * unified OR of the two independent staleness signals the list already
     * exposed separately (`app_role === null` after a SET_NULL catalog delete OR
     * `app_role_active === false` after the mark-and-sweep sync deactivated it).
     * Callers reading only ONE of the two rendered a broken mapping as healthy;
     * prefer this field. The two originals stay untouched, so nothing that read
     * them before needs to change.
     */
    is_stale: boolean;
}

/**
 * EAA-1837: shared response shape of `bulk-scope` and `bulk-delete`.
 * `skipped` holds ids the acting org does not own — they MUST be surfaced, not
 * silently dropped (the server deliberately reports them instead of 500ing).
 */
export interface RoleMapBulkResult {
    updated: string[];
    skipped: string[];
}

/** EAA-1837: one entry of `bulk-sync`'s `results` — `RoleMapService.resync_row`'s
 *  recomputed health for a single row, verified field-for-field against that
 *  method in `directory/services.py`. */
export interface RoleMapResyncResult {
    id: string;
    role_key: string;
    application: string;
    organization_id: string | null;
    is_stale: boolean;
    /** Server-side `resolve_for_provisioning` reason slug (e.g. `ok`, `app_role_removed`). */
    reason: string;
    app_roles: string[];
}

export interface RoleMapBulkSyncResult {
    results: RoleMapResyncResult[];
    skipped: string[];
}

export interface PausedMapping {
    id: string;
    role_key: string;
    application: string;
    organization_id: string | null;
    app_role: string | null;
    reason: string;
}

// EAA-1814: Level whose role_key currently grants nothing anywhere (F4 orphan report).
// Shape confirmed against LevelService.orphan_levels() in
// services/portal-api/portal_api/directory/services.py (~L544-570) — every field the
// service builds is listed here, nothing inferred.
export interface OrphanLevel {
    level_id: string;
    name: string;
    role_key: string;
    organization_id: string;
    organization_code: string;
    /** 'no_scopes' is informational (pre-backfill), not a defect; the other two are. */
    reason: 'no_scopes' | 'no_mapping' | 'paused';
}

export interface RoleMapSyncStatus {
    status: 'ok' | 'degraded';
    healthy_count: number;
    paused_count: number;
    paused: PausedMapping[];
    // EAA-1814: added — RoleMapService.sync_status() (services.py ~L702-711) also
    // returns these two fields (Levels that resolve to no app role anywhere); the
    // console previously had no way to see orphan Levels at all.
    orphan_level_count: number;
    orphan_levels: OrphanLevel[];
    /**
     * EAA-1817: the master switch (`PROVISIONING_ENABLED`) as it is ACTUALLY in
     * force — a confirmed console override beats the env var, which is exactly how
     * a deployment can read "true" in its environment and still write nothing.
     *
     * When this is false every push is refused and the refusal is swallowed, so
     * affected Assignments sit at `sync_status: 'pending'` with an EMPTY
     * `last_sync_error` — indistinguishable, on screen, from "queued".
     */
    provisioning_enabled: boolean;
    /** How many live grants are waiting on that switch. 0 when it is on. */
    pending_assignment_count: number;
}

export interface CatalogSyncResult {
    application: string;
    // EAA-1817: 'preview' only ever appears in a dry-run response (see
    // `triggerCatalogSync`'s `dryRun` param) — a live sync never returns it.
    status: 'synced' | 'skipped' | 'preview';
    active_roles?: number;
    /**
     * EAA-1817: what a LIVE sync changed — the app-role keys that arrived and
     * the ones the app stopped reporting. Present on `status === 'synced'` only
     * (a skip changed nothing; a preview uses `would_add`/`would_deactivate`).
     *
     * `deactivated` is the operationally urgent half: every link pointing at one
     * of those keys is now paused and granting nothing.
     */
    added?: string[];
    deactivated?: string[];
    reason?: string;
    // EAA-1817: preview-only fields, populated when status === 'preview' and
    // absent otherwise (mirrors `active_roles`/`reason` being synced/skipped-only).
    would_add?: string[];
    would_deactivate?: string[];
    would_pause?: string[];
    would_pause_count?: number;
}

/** EAA-1817: `POST /api/v1/role-map/sync?dry_run=true` top-level response shape. */
export interface CatalogSyncDryRunResponse {
    dry_run: true;
    results: CatalogSyncResult[];
    would_pause_count: number;
}

export interface AccountIssued {
    sub: string;
    username: string;
    /** One-time credential — rendered exactly once, never retrievable again (D12). */
    temp_password: string | null;
}

/** EAA-1779: which credential the reset set — see `resetAccountPassword`. */
export type AccountResetMode = 'temp_password' | 'admin_set';

export interface AccountPasswordReset {
    sub: string;
    username: string;
    mode: AccountResetMode;
    /**
     * One-time temp password — shown once; NULL in `admin_set` mode, where the
     * operator already has the value and the server deliberately does not echo
     * it back. Branch on `mode`, not on this being falsy.
     */
    temp_password: string | null;
    /** Whether the user must still change the password at next login. */
    force_change?: boolean;
    /** True when Keycloak logout-all ran after reset (EAA-1820, frame 74). */
    sessions_ended?: boolean;
}

export interface AuditRow {
    id: string;
    created_at: string;
    actor_sub: string;
    actor_label?: string;
    action: 'create' | 'delete';
    target_sub: string;
    username: string;
    email: string;
    credential_mode: string;
    success: boolean;
    detail: Record<string, unknown>;
}

// EAA-1814: mirrors AdminAuditSerializer in
// services/portal-api/portal_api/directory/org_serializers.py (~L381-392) field for
// field — every name below is a confirmed server field, not inferred.
export interface AdminAuditRow {
    id: string;
    created_at: string;
    actor_sub: string;
    actor_label: string;
    action: string;
    target_type: string;
    target_id: string;
    success: boolean;
    detail: Record<string, unknown>;
}

// EAA-1814: `/accounts/audit` (identity writes) and `/admin/audit` (non-identity admin
// writes) are separate append-only tables with different row shapes — this is the
// common projection a "recent activity" widget can merge across both without caring
// which table a row came from.
export interface UnifiedAuditRow {
    id: string | number;
    created_at: string;
    actor_sub: string;
    actor_label: string;
    action: string;
    success: boolean;
    detail?: string;
    kind: 'account' | 'admin';
    /** Human-readable target, derived per source by the mapper. */
    target: string;
    /**
     * EAA-1821: `credential_mode` was always on the wire for account-audit rows
     * (`AuditRow.credential_mode`) but the unified projection dropped it, so the
     * audit screen had no way to render — or filter on — the "Chế độ" column
     * without re-fetching the raw row. Surfaced here instead. Optional because
     * admin-audit rows have no equivalent field; `toUnifiedFromAdminAudit`
     * leaves it undefined and the column renders empty for those rows.
     */
    credential_mode?: string;
}

// EAA-1814: pure, side-effect-free projection — no fetch, no pagination. Each audit
// source paginates independently server-side (different tables, different filters),
// so there is no single "page 2 of both" the client can derive; do NOT build a
// combined-pagination helper on top of these. They exist to feed a "recent N" widget
// that fetches a small page from each source and interleaves by created_at.
export function toUnifiedFromAccountAudit(row: AuditRow): UnifiedAuditRow {
    return {
        id: row.id,
        created_at: row.created_at,
        actor_sub: row.actor_sub,
        actor_label: row.actor_label ?? '',
        action: row.action,
        success: row.success,
        detail: JSON.stringify(row.detail),
        kind: 'account',
        target: row.username || row.target_sub,
        // EAA-1821: surfaces the "Chế độ" column — see UnifiedAuditRow.credential_mode.
        credential_mode: row.credential_mode,
    };
}

// EAA-1814: see toUnifiedFromAccountAudit — same constraint (no combined pagination).
export function toUnifiedFromAdminAudit(row: AdminAuditRow): UnifiedAuditRow {
    return {
        id: row.id,
        created_at: row.created_at,
        actor_sub: row.actor_sub,
        actor_label: row.actor_label ?? '',
        action: row.action,
        success: row.success,
        detail: JSON.stringify(row.detail),
        kind: 'admin',
        target: `${row.target_type}:${row.target_id}`,
    };
}

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    data: T;
    // EAA-1884: `emg_shared.api.responses.error_response` puts validation-error
    // detail here, TOP-LEVEL, alongside `data: null` — see the fix note on
    // `request()` below. Not typed further than `unknown`: shape varies per
    // endpoint (a `Record<string, ({code,message} | string)[]>`), same as
    // `orgAdmin.ts`'s own `ApiEnvelope`.
    details?: unknown;
}

export interface Paginated<T> {
    results: T[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

async function request<T>(
    apiBase: string,
    token: string,
    path: string,
    init?: { method?: string; body?: unknown; orgId?: string }
): Promise<T> {
    // EAA-1797 (P4b): send the console's acting org on every request as X-Org-Id
    // (see orgAdmin.ts for the rationale) — the server re-authorizes it (P4a).
    const orgId = init?.orgId ?? useSessionStore.getState().actingOrgId;
    // EAA-1825: `authFetch` retries once with a renewed token on 401.
    const res = await authFetch(`${apiBase}${path}`, token, {
        method: init?.method ?? 'GET',
        headers: {
            ...(orgId ? { 'X-Org-Id': orgId } : {}),
            ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    if (res.status === 204) {
        return undefined as T;
    }
    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
    if (!res.ok || !envelope?.success) {
        // EAA-1884: was `envelope?.data` — a copy/paste drift from a build where
        // an error envelope's payload rode on `data`. It never has since
        // (`error_response()` always sends `data: null` on failure and puts
        // validation detail on a TOP-LEVEL `details` key — confirmed against
        // `emg_shared/api/responses.py`), so every existing `.details` consumer
        // of THIS `request()` (AppOnboardingModal's keycloak error,
        // AppDetailConnectionTab's cooldown countdown, useEditAppForm's field
        // errors) has been reading `null` all along and silently falling back to
        // the generic message. `orgAdmin.ts`'s sibling `request()` already reads
        // `envelope?.details` correctly — this brings the two back in sync.
        // Fixing it is required for this ticket's retire-dependencies flow
        // (`RetireAppModal`) to work at all, so it is done here rather than
        // deferred.
        throw new OrgAdminApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            envelope?.details
        );
    }
    return envelope.data;
}

/**
 * EAA-1884: flattens a raw `OrgAdminApiError.details` payload into the
 * `Record<string, string[]>` shape every form's `fieldErrors` state already
 * expects. Needed because of a second, related fact the `request()` fix above
 * exposed: each list ENTRY in `details` is a `{code, message}` object, not a
 * bare string (`emg_shared/api/validation_envelope
 * .normalize_manual_error_details` wraps every one), so passing `details`
 * straight into a `Record<string, string[]>`-typed state used to typecheck
 * but would render raw objects — a crash under `FormFieldMessage`'s
 * `{message}` — the moment `.details` stopped being `null`. Every call site
 * that reads `error.details` after an `OrgAdminApiError` from this module's
 * `request()` should route through this rather than re-deriving the same cast.
 */
export function normalizeApiErrorDetails(details: unknown): Record<string, string[]> {
    if (!details || typeof details !== 'object') return {};
    const out: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
        if (!Array.isArray(value)) continue;
        out[key] = value.map((item) =>
            typeof item === 'string' ? item : ((item as { message?: string } | null)?.message ?? '')
        );
    }
    return out;
}

/* ---- Registry admin (Ứng dụng) ---- */

// EAA-1817: return type widened `RegistryApp[]` -> `AdminRegistryApp[]` — this
// route really does inject `role_link_count`/`catalog_role_count` server-side
// (see `AdminRegistryApp` above). A pure widening: `AdminRegistryApp` is a
// subtype of `RegistryApp`, so every existing caller still typechecks.
export function fetchAdminApps(apiBase: string, token: string): Promise<AdminRegistryApp[]> {
    return request<AdminRegistryApp[]>(apiBase, token, '/api/v1/admin/apps');
}

/**
 * EAA-1817: the registration fields portal-api's `AdminAppCreateSerializer`
 * accepts, beyond the original five. Declared explicitly rather than left off:
 * without them a caller building the payload as an inline literal trips
 * TypeScript's excess-property check, and the tempting workaround — hoisting the
 * object to a `const` so the check does not fire — ships fields the type says do
 * not exist. That silently decouples the client type from the wire format.
 *
 * `code` is create-only (immutable afterwards) and `enabled` is PATCH-only (it is
 * the D11 production gate; create always forces `false` server-side), which is
 * why the two bodies below don't share one type.
 */
interface AdminAppWritableFields {
    name?: string;
    web_url?: string;
    api_base?: string;
    audience?: string;
    description?: string;
    icon_url?: string;
    app_type?: AppType;
    owner_sub?: string;
    owner_name?: string;
    owner_email?: string;
    redirect_uris?: string[];
    post_logout_redirect_uris?: string[];
    org_scope_mode?: OrgScopeMode;
    // EAA-1841: accepted by BOTH `AdminAppCreateSerializer` and
    // `AdminAppUpdateSerializer`, so it belongs in the shared writable set
    // rather than beside `enabled` (which is PATCH-only).
    supports_role_management?: boolean;
}

/**
 * EAA-1709: what `POST /admin/apps` and `PATCH /admin/apps/{code}` did to the
 * app's Keycloak clients. Client creation is automatic on both calls (web →
 * login client; hybrid → login + `<code>-api`; api → `<code>-api` only), and a
 * Keycloak failure is REPORTED here rather than failing the registry write that
 * already succeeded.
 */
export type AppProvisioningStatus = 'ok' | 'changed' | 'reported' | 'skipped' | 'failed';

export interface AppProvisioningStep {
    step: string;
    /** Per-step outcome (`created`, `adopted`, `ok`, `skipped`, `failed`, …). */
    status: string;
    detail: string;
}

export interface AppProvisioning {
    status: AppProvisioningStatus;
    steps: AppProvisioningStep[];
    /**
     * Non-null EXACTLY ONCE — on the call that created `<code>-api` — and only
     * for a caller holding `console.apps.credentials`. Plaintext, never
     * returned again: same one-time-reveal contract as `createAppClient`.
     */
    credentials: { client_id: string; client_secret: string } | null;
}

/** EAA-1709: the registry row as create/update answer it — the row plus `provisioning`. */
export type ProvisionedRegistryApp = RegistryApp & { provisioning?: AppProvisioning };

export function createAdminApp(
    apiBase: string,
    token: string,
    body: AdminAppWritableFields & { code: string; name: string }
): Promise<ProvisionedRegistryApp> {
    return request<ProvisionedRegistryApp>(apiBase, token, '/api/v1/admin/apps', {
        method: 'POST',
        body,
    });
}

export function updateAdminApp(
    apiBase: string,
    token: string,
    code: string,
    body: AdminAppWritableFields & { enabled?: boolean }
): Promise<ProvisionedRegistryApp> {
    return request<ProvisionedRegistryApp>(apiBase, token, `/api/v1/admin/apps/${code}`, {
        method: 'PATCH',
        body,
    });
}

/**
 * EAA-1709: `POST /api/v1/admin/apps/{code}/approve` — the production approval
 * gate. The server runs the connection check ITSELF and refuses (VALIDATION_ERROR,
 * `details.connection_check: ["<probe>: <detail>", …]`) while any probe is
 * `failed`; `degraded` does not block. Already-enabled is an idempotent success.
 * The console never sends `force` — that override is for incident restores and
 * has no input here.
 */
export function approveApp(
    apiBase: string,
    token: string,
    code: string
): Promise<{
    application: string;
    enabled: boolean;
    already_enabled: boolean;
    forced?: boolean;
    connection_check: ConnectionCheckReport;
    checklist: AppChecklist;
}> {
    return request(apiBase, token, `/api/v1/admin/apps/${encodeURIComponent(code)}/approve`, {
        method: 'POST',
        body: {},
    });
}

/**
 * EAA-1817/EAA-1810 (G1): `POST /admin/apps/{code}/suspend` — distinct from the
 * D11 `enabled` flip above: it demands a non-blank `reason` and preserves the
 * app's approval history (`lifecycle` moves to `suspended`, not back to
 * `pending`). Only callable on an `active` app; the server 400s otherwise.
 */
export function suspendApp(
    apiBase: string,
    token: string,
    code: string,
    reason: string
): Promise<RegistryApp> {
    return request<RegistryApp>(apiBase, token, `/api/v1/admin/apps/${code}/suspend`, {
        method: 'POST',
        body: { reason },
    });
}

/**
 * EAA-1817/EAA-1810 (G1): `POST /admin/apps/{code}/resume` — undoes a
 * suspension (`lifecycle` back to `active`). Only callable on a `suspended`
 * app; a `retired` app cannot come back this way (its Keycloak client was
 * already disabled) and must be re-approved through the D11 flip instead.
 */
export function resumeApp(apiBase: string, token: string, code: string): Promise<RegistryApp> {
    return request<RegistryApp>(apiBase, token, `/api/v1/admin/apps/${code}/resume`, {
        method: 'POST',
    });
}

/**
 * EAA-1817/EAA-1810 (G1): `DELETE /admin/apps/{code}` — SOFT deregistration
 * (`lifecycle` → `retired`, row retained, Keycloak client disabled). The
 * server refuses when `RoleMap`/`Assignment`/audit rows still reference the
 * app UNLESS `force` is passed — on that refusal it returns a 400 whose
 * `error.details.dependencies` names each live reference type + count, and
 * `error.details.force` repeats the hint. Callers should catch
 * `OrgAdminApiError`, read `.details`, and re-call with `force: true` only
 * after the operator has seen and confirmed those counts.
 */
export function retireApp(
    apiBase: string,
    token: string,
    code: string,
    options?: { force?: boolean }
): Promise<RegistryApp> {
    const query = options?.force ? '?force=true' : '';
    return request<RegistryApp>(apiBase, token, `/api/v1/admin/apps/${code}${query}`, {
        method: 'DELETE',
    });
}

/* ---- Role map ---- */

/**
 * EAA-1817: extended with optional `includeInactive` (`include_inactive=true`)
 * and `organization` (`organization={uuid}`) params — both additive, so
 * existing 2-arg-plus-appCode callers are unaffected.
 *
 * GATE: `/admin/app-roles` is `@require_permissions_for_method(get=(APPS_VIEW,),
 * post=(APPS_MANAGE,))` in portal-api.
 *
 * EAA-1817 CORRECTION: this comment used to read "`IsSuperAdmin` ONLY … even
 * though the SRS claims org-admins get their own org's catalog here too — an
 * org-admin call 403s", and told callers to trust it over the SRS. It was wrong
 * on both counts: there is no superadmin gate, and an org-admin holding
 * `APPS_VIEW` is served, so the SRS was right. What survives is the real
 * constraint — this is an APPS permission, not a ROLEMAP one, so a role-map
 * screen cannot assume its own reader may call it.
 */
export function fetchAppRoles(
    apiBase: string,
    token: string,
    appCode: string,
    params?: { includeInactive?: boolean; organization?: string }
): Promise<AppRoleOption[]> {
    const query = new URLSearchParams({ application: appCode });
    if (params?.includeInactive) query.set('include_inactive', 'true');
    if (params?.organization) query.set('organization', params.organization);
    return request<AppRoleOption[]>(apiBase, token, `/api/v1/admin/app-roles?${query.toString()}`);
}

/**
 * EAA-1841: `POST /api/v1/admin/app-roles` — define a role IN the app's own
 * catalog, rather than only reading what it already declares.
 *
 * Closes the dead-end this console had until now: `POST /role-maps` is
 * fail-closed and refuses any `app_role` the app does not declare, so a missing
 * role meant leaving the console, signing into the app with that app's own
 * admin rights, creating the role by hand, and coming back to re-sync.
 *
 * Two properties of the server contract shape every caller here:
 *
 * 1. **`organization_id` is required and is an ORGANIZATION, never a scope id.**
 *    The role lives in an app-local scope (for ACH, a tenant); portal-api
 *    resolves org → scope through `OrganizationAppScope` itself, precisely so a
 *    caller cannot write into an arbitrary tenant. An org with no active scope
 *    for this app is a `VALIDATION_ERROR`, not a silent no-op.
 * 2. **The row it upserts locally is ORG-SCOPED** (`AppRole.organization=org`),
 *    while `fetchAppRoles` defaults to ecosystem-wide rows
 *    (`organization__isnull=True`). A picker that does not pass `organization`
 *    will therefore NOT show a role created here — callers must pass it, or
 *    the operator creates a role and watches it fail to appear.
 *
 * `permissions` is optional and omitting it is the safe default: the receiver
 * refuses unknown permission codes outright, and there is no endpoint exposing
 * an app's valid codes, so a role is created empty and its permissions are set
 * inside the app itself. A group with no permissions grants nothing.
 */
export interface CreateAppRoleBody {
    application: string;
    organization_id: string;
    /** App-local identifier — a slug, because provisioning later pushes it back
     *  verbatim; free text here produces a role nothing can resolve. */
    key: string;
    name: string;
    description?: string;
    permissions?: string[];
}

export function createAppRole(
    apiBase: string,
    token: string,
    body: CreateAppRoleBody
): Promise<AppRoleOption> {
    return request<AppRoleOption>(apiBase, token, '/api/v1/admin/app-roles', {
        method: 'POST',
        body,
    });
}

/**
 * EAA-1817: `PATCH /api/v1/admin/app-roles/{id}` (EAA-1709, gap G9) — edit a
 * catalog row's LOCAL display name / permission snapshot. Unlike
 * `createAppRole`, this never touches the app itself: it corrects portal-api's
 * own cached copy of what the app already reported, so — unlike create —
 * `permissions` IS safe to edit here. `key` and `is_active` are not accepted;
 * the server ignores them rather than rejecting the request (see
 * `AppRoleService.EDITABLE_FIELDS`).
 */
export function updateAppRole(
    apiBase: string,
    token: string,
    roleId: string,
    body: { name?: string; permissions?: string[] }
): Promise<AppRoleOption> {
    return request<AppRoleOption>(apiBase, token, `/api/v1/admin/app-roles/${roleId}`, {
        method: 'PATCH',
        body,
    });
}

/**
 * EAA-1817: extended with optional `includeDeleted` (`include_deleted=true`).
 * Default is unchanged — omitting the param still excludes soft-deleted rows
 * (`deleted_at__isnull=True` server-side), so no current caller's behaviour
 * changes.
 */
export function fetchRoleMaps(
    apiBase: string,
    token: string,
    params?: { includeDeleted?: boolean }
): Promise<RoleMapRow[]> {
    const query = params?.includeDeleted ? '?include_deleted=true' : '';
    return request<RoleMapRow[]>(apiBase, token, `/api/v1/role-maps${query}`);
}

export function createRoleMap(
    apiBase: string,
    token: string,
    body: { role_key: string; application: string; app_role: string; organization_id?: string }
): Promise<{ id: string }> {
    return request<{ id: string }>(apiBase, token, '/api/v1/role-maps', { method: 'POST', body });
}

/**
 * EAA-1817: this is now a SOFT delete server-side (`is_active=False,
 * deleted_at=now()` in `RoleMapService.delete`) — still returns a bare 204
 * with no body, so `request()`'s existing `res.status === 204 -> undefined`
 * short-circuit already handles it correctly with no change needed here. See
 * `restoreRoleMap` for the undo path this soft delete now enables.
 */
export function deleteRoleMap(apiBase: string, token: string, id: string): Promise<void> {
    return request<void>(apiBase, token, `/api/v1/role-maps/${id}`, { method: 'DELETE' });
}

export function fetchRoleMapSyncStatus(apiBase: string, token: string): Promise<RoleMapSyncStatus> {
    return request<RoleMapSyncStatus>(apiBase, token, '/api/v1/role-map/sync-status');
}

/**
 * EAA-1817: extended with optional `dryRun` (`dry_run=true`) — a read-only
 * preview pass that performs no writes; its `results` rows carry
 * `status: 'preview'` plus the `would_*` fields instead of `active_roles`/
 * `reason`. Overloaded so a `{ dryRun: true }` call is typed with
 * `CatalogSyncDryRunResponse` and a plain call keeps the original shape.
 */
export function triggerCatalogSync(
    apiBase: string,
    token: string
): Promise<{ results: CatalogSyncResult[] }>;
export function triggerCatalogSync(
    apiBase: string,
    token: string,
    params: { dryRun: true }
): Promise<CatalogSyncDryRunResponse>;
export function triggerCatalogSync(
    apiBase: string,
    token: string,
    params?: { dryRun?: boolean }
): Promise<{ results: CatalogSyncResult[] } | CatalogSyncDryRunResponse> {
    const query = params?.dryRun ? '?dry_run=true' : '';
    return request(apiBase, token, `/api/v1/role-map/sync${query}`, { method: 'POST' });
}

/* ---- User sync (EAA-1861) ---- */

export interface UserSyncError {
    external_id: string;
    error: string;
    /** EAA-1883: structured classification of `error` — see
     *  `@/pages/console/apps/syncErrorMessages`'s `SyncErrorCode` for the
     *  known values. Absent/`null` on rows the backend has not classified. */
    error_code?: string | null;
    /** EAA-1883: present alongside `error_code: "unresolved_scope"` — the
     *  scope id the record carried that no synced `Organization` owns yet. */
    scope_id?: string;
}

/** EAA-1883: `no_scope_id` (a record with no school/org identifier at all —
 *  a system-wide admin role, or a service/automation account) moved OUT of
 *  `errors: []` into this sibling list. It is a terminal, non-actionable
 *  state — unlike `unresolved_scope` ("sync organizations first"), there is
 *  nothing an operator can do to "fix" a system admin into having a school —
 *  so showing it as a red error was misleading. Same per-row shape as
 *  `UserSyncError` (minus `scope_id`, which only applies to `unresolved_scope`). */
export interface UserSyncSkipped {
    external_id: string;
    error: string;
    error_code: 'no_scope_id';
}

export interface UserSyncResult {
    created: number;
    linked: number;
    errors: UserSyncError[];
    /** EAA-1883: see `UserSyncSkipped` — rendered as its own neutral section,
     *  never inside the `errors` list. */
    skipped: UserSyncSkipped[];
}

/**
 * EAA-1886: the POST no longer returns the counts. Minting Keycloak
 * identities inline for 100+ users blew gunicorn's worker timeout, so the
 * endpoint now enqueues a job and answers 202 with only the handle below;
 * the counts arrive from `fetchUserSyncJob` once the job reaches a terminal
 * status.
 */
export interface UserSyncJobHandle {
    job_id: string;
    status: string;
}

/** EAA-1886: `UserSyncJob.STATUS_*` in portal-api has exactly four values —
 *  `pending` | `running` | `completed` | `failed`. Only the last two are
 *  terminal: a job sits at `pending` until a worker picks it up, which can be
 *  a while, so `pending` must keep polling exactly like `running`. */
export type UserSyncJobState = 'pending' | 'running' | 'completed' | 'failed';

export const USER_SYNC_TERMINAL_STATUSES = ['completed', 'failed'] as const;

export function isUserSyncTerminalStatus(status: string | undefined): boolean {
    return (
        status !== undefined && (USER_SYNC_TERMINAL_STATUSES as readonly string[]).includes(status)
    );
}

export function isUserSyncFailedStatus(status: string | undefined): boolean {
    return status === 'failed';
}

/** EAA-1886: `UserSyncJob.progress` (a Python property) counts BATCHES, not
 *  individual records — `total` is how many records were fetched from the
 *  source app, while the moving parts are `batches_completed`/`batches_total`
 *  and the pre-rounded `percentage` (float, 1dp; `0.0` while `batches_total`
 *  is 0). There is no per-record "processed" counter to render. */
export interface UserSyncJobProgress {
    total?: number;
    batches_total?: number;
    batches_completed?: number;
    percentage?: number;
}

/**
 * EAA-1886: the poll response. It carries the same four result keys the POST
 * used to return synchronously, so a caller that reads only those keeps
 * working once the job completes — but every one of them is OPTIONAL here,
 * because a `pending`/`running` job has no counts yet. Treat them as absent
 * until `isUserSyncTerminalStatus(status)`.
 */
export interface UserSyncJobStatus {
    job_id?: string;
    status: UserSyncJobState | string;
    progress?: UserSyncJobProgress | null;
    created?: number;
    linked?: number;
    errors?: UserSyncError[];
    skipped?: UserSyncSkipped[];
    /** EAA-1886: populated ONLY on a whole-job failure before any batch ran
     *  (e.g. a `ScimNotConfigured` message) — the real cause, and far more
     *  useful to an operator than the generic failure copy, so surface it
     *  verbatim whenever it is non-empty. */
    error_detail?: string | null;
}

/** EAA-1861: live pull from the app's own `GET /api/v1/directory/users` —
 *  data-driven per `Application`, so a second app is a DB row, not a new
 *  client function. EAA-1886: now asynchronous — resolves with the job handle,
 *  not the result. */
export function syncAppUsers(
    apiBase: string,
    token: string,
    code: string
): Promise<UserSyncJobHandle> {
    return request<UserSyncJobHandle>(apiBase, token, `/api/v1/admin/apps/${code}/sync-users`, {
        method: 'POST',
    });
}

/** EAA-1886: poll one enqueued user-sync job for progress and, once terminal,
 *  the `created`/`linked`/`errors`/`skipped` result the POST used to return.
 *  The endpoint answers through `success_response`, so the shape above is the
 *  envelope's `data` — which the shared `request()` already unwraps. */
export function fetchUserSyncJob(
    apiBase: string,
    token: string,
    code: string,
    jobId: string
): Promise<UserSyncJobStatus> {
    return request<UserSyncJobStatus>(
        apiBase,
        token,
        `/api/v1/admin/apps/${code}/sync-users/jobs/${jobId}`
    );
}

/* ---- Organization sync (EAA-1878 WP-4) ---- */

/**
 * EAA-1878: `OrganizationSyncService._sync_records` (portal-api) tags each
 * error row with EITHER `organization_id` (root-level row failures) OR
 * `school_id` (child-row failures) — never both — so both keys are optional
 * here rather than a single shared `external_id` like `UserSyncError`. Read
 * whichever key is present when rendering a row.
 */
export interface OrganizationSyncError {
    organization_id?: string;
    school_id?: string;
    error: string;
    /** EAA-1883: structured classification of `error` — see
     *  `@/pages/console/apps/syncErrorMessages`'s `SyncErrorCode` for the
     *  known values. Absent/`null` on rows the backend has not classified. */
    error_code?: string | null;
    /** EAA-1883: present alongside `error_code: "unresolved_scope"` — the
     *  scope id the record carried that no synced `Organization` owns yet. */
    scope_id?: string;
}

/** EAA-1878: one field richer than `UserSyncResult` — org-sync can update and
 *  deactivate existing rows, not only create new ones (mark-and-sweep). */
export interface OrganizationSyncResult {
    created: number;
    updated: number;
    deactivated: number;
    errors: OrganizationSyncError[];
}

/**
 * EAA-1878: mirrors `syncAppUsers` exactly — live pull from the app's own
 * `GET /api/v1/management/organizations`, data-driven per `Application`.
 *
 * EAA-1887: `rootOrganizationId` is optional — an existing Portal root
 * Organization the admin picked beforehand (see `useRootOrganizationOptions`).
 * When given, every top-level synced entry is parented under it instead of
 * becoming its own new root; omitted keeps the EAA-1878 behaviour unchanged.
 * The key is left OFF the body entirely when unset, matching every other
 * optional-field POST in this file, rather than sending an explicit `null`.
 */
export function syncAppOrganizations(
    apiBase: string,
    token: string,
    code: string,
    rootOrganizationId?: string
): Promise<OrganizationSyncResult> {
    return request<OrganizationSyncResult>(apiBase, token, `/api/v1/admin/apps/${code}/sync-organizations`, {
        method: 'POST',
        body: rootOrganizationId ? { root_organization_id: rootOrganizationId } : {},
    });
}

/**
 * EAA-1861: same identity-minting logic as `syncAppUsers`, sourced from an
 * uploaded CSV instead — for an app with no pull endpoint yet. Bypasses the
 * shared `request()` helper deliberately: that helper always JSON-stringifies
 * the body and forces `Content-Type: application/json`, which would corrupt
 * a multipart upload (the browser must set that header itself, boundary
 * included).
 */
export async function importAppUsers(
    apiBase: string,
    token: string,
    code: string,
    file: File
): Promise<UserSyncResult> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await authFetch(`${apiBase}/api/v1/admin/apps/${code}/import-users`, token, {
        method: 'POST',
        body: formData,
    });
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<UserSyncResult> | null;
    if (!res.ok || !envelope?.success) {
        throw new OrgAdminApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            (envelope as { data?: unknown } | null)?.data
        );
    }
    return envelope.data;
}

/* ---- Role map bulk admin (EAA-1837, org-scoped) ----
 *
 * All four endpoints below live under `/organizations/{orgId}/role-map/...` and
 * operate ONLY on rows that org OWNS as an explicit override — the server's
 * `RoleMapService.list_for_organization` deliberately EXCLUDES ecosystem-wide
 * defaults (`organization_id === null`), and `bulk_set_scope`/`bulk_deactivate`
 * filter on `organization_id=org_id`. Passing a default row's id therefore
 * always comes back in `skipped`, never applied. The UI must not offer these
 * actions while the "Mặc định" scope is selected — see `useRoleMapTable.ts`.
 */

/**
 * EAA-1837: "Đổi phạm vi" — the only legal `organization_id` values are `null`
 * (promote to the ecosystem default) or `orgId` itself (keep as this org's own
 * override). A genuinely different org id is refused server-side with
 * VALIDATION_ERROR, which is why this is a binary toggle in the UI and not an
 * arbitrary org picker.
 */
export function bulkSetRoleMapScope(
    apiBase: string,
    token: string,
    orgId: string,
    body: { ids: string[]; organization_id: string | null }
): Promise<RoleMapBulkResult> {
    return request<RoleMapBulkResult>(
        apiBase,
        token,
        `/api/v1/organizations/${encodeURIComponent(orgId)}/role-map/bulk-scope`,
        { method: 'POST', body, orgId }
    );
}

/** EAA-1837: "Đồng bộ lại" — recomputes each row's health against the live app-role
 *  catalog. A read-recompute, never a write to the mapping itself. */
export function bulkResyncRoleMaps(
    apiBase: string,
    token: string,
    orgId: string,
    ids: string[]
): Promise<RoleMapBulkSyncResult> {
    return request<RoleMapBulkSyncResult>(
        apiBase,
        token,
        `/api/v1/organizations/${encodeURIComponent(orgId)}/role-map/bulk-sync`,
        { method: 'POST', body: { ids }, orgId }
    );
}

/** EAA-1837: "Xoá" — a SOFT delete (`is_active=False`), matching the Level /
 *  Assignment / OrganizationAdmin convention throughout portal-api. */
export function bulkDeleteRoleMaps(
    apiBase: string,
    token: string,
    orgId: string,
    ids: string[]
): Promise<RoleMapBulkResult> {
    return request<RoleMapBulkResult>(
        apiBase,
        token,
        `/api/v1/organizations/${encodeURIComponent(orgId)}/role-map/bulk-delete`,
        { method: 'POST', body: { ids }, orgId }
    );
}

/**
 * EAA-1837: "Xuất CSV" — this is the ONE endpoint in this file that does NOT
 * answer with the `{success, code, data}` envelope: it streams a real
 * `text/csv` attachment, so it cannot go through `request()` (which would try
 * to `res.json()` the CSV body and throw). Kept as a raw fetch + Blob +
 * synthetic `<a download>` click, the same shape `accounts/BulkResetResultPanel.tsx`
 * already uses for its client-built CSV — only the bytes come from the server here.
 *
 * `ids` is optional: omit it to export every row in the org's scope.
 */
export async function downloadRoleMapCsv(
    apiBase: string,
    token: string,
    orgId: string,
    ids?: string[],
    /**
     * EAA-1817 (frame 55c, "Kèm cột mã vai trò"): add an `app_role_key` column.
     * The server's existing `app_role` column holds display NAMES despite its
     * header, so the keys are a separate, explicitly-named column.
     */
    includeCodes = false
): Promise<void> {
    const query = new URLSearchParams();
    if (ids && ids.length > 0) query.set('ids', ids.join(','));
    if (includeCodes) query.set('include_codes', 'true');
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const res = await authFetch(
        `${apiBase}/api/v1/organizations/${encodeURIComponent(orgId)}/role-map/export${suffix}`,
        token,
        { headers: { 'X-Org-Id': orgId } }
    );
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    if (!res.ok) {
        // A failure here still comes back as the JSON envelope (DRF error
        // responses are never CSV), so read it that way for the message.
        const envelope = (await res.json().catch(() => null)) as ApiEnvelope<unknown> | null;
        throw new OrgAdminApiError(
            envelope?.description ?? `Export failed (${res.status})`,
            envelope?.code ?? res.status
        );
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filenameFromDisposition(res.headers.get('Content-Disposition'));
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}

function filenameFromDisposition(header: string | null): string {
    const match = header?.match(/filename="?([^";]+)"?/);
    return match?.[1] ?? `role-map-${Date.now()}.csv`;
}

/* ---- Role map coverage board (EAA-1817, pairs with portal-api EAA-1840) ---- */

export interface CoverageApplication {
    code: string;
    name: string;
    enabled: boolean;
}

export type CoverageCellState = 'linked' | 'multi' | 'paused' | 'none';

export interface CoverageCell {
    state: CoverageCellState;
    count: number;
    /** App-role KEYS, sorted. */
    roles: string[];
    /**
     * EAA-1817: display names, INDEX-FOR-INDEX with `roles` — a caller zips the
     * two together to label a cell and put the key in its tooltip. Same length
     * as `roles` always, including empty.
     */
    role_names: string[];
}

/**
 * EAA-1817: `GET /api/v1/role-map/coverage` response — the role x application
 * coverage board. `cells` is keyed by `coverageCellKey(app.code, roleKey)`.
 */
export interface RoleMapCoverage {
    applications: CoverageApplication[];
    role_keys: string[];
    cells: Record<string, CoverageCell>;
}

/** EAA-1817: builds a coverage cell key — callers must never hand-concatenate
 *  `${app_code}:${role_key}` themselves. Pair with `parseCoverageCellKey`. */
export function coverageCellKey(appCode: string, roleKey: string): string {
    return `${appCode}:${roleKey}`;
}

/** EAA-1817: inverse of `coverageCellKey`. `roleKey` may itself contain `:`
 *  in theory, so this splits on the FIRST separator only. */
export function parseCoverageCellKey(cellKey: string): { appCode: string; roleKey: string } {
    const separatorIndex = cellKey.indexOf(':');
    return {
        appCode: cellKey.slice(0, separatorIndex),
        roleKey: cellKey.slice(separatorIndex + 1),
    };
}

/**
 * EAA-1817: `GET /api/v1/role-map/coverage` — `RoleMapService.coverage()`
 * filters `organization__isnull=True` server-side, so this board only EVER
 * reflects ECOSYSTEM-DEFAULT rows — it never shows per-org overrides. Do not
 * add an `organization` param here; the endpoint has none.
 */
export function fetchRoleMapCoverage(apiBase: string, token: string): Promise<RoleMapCoverage> {
    return request<RoleMapCoverage>(apiBase, token, '/api/v1/role-map/coverage');
}

/* ---- Role-key vocabulary (EAA-1817) ---- */

export interface RoleKeyRow {
    key: string;
    name: string;
    description: string;
    is_active: boolean;
    sort_order: number;
    /** EAA-1817: server-computed by `StandardRoleService.usage_counts()` — never derive client-side. */
    link_count: number;
    /** EAA-1817: server-computed alongside `link_count` — never derive client-side. */
    level_count: number;
}

/** EAA-1817: `GET /api/v1/role-keys` — the governed role-key vocabulary rows. */
export function fetchRoleKeys(apiBase: string, token: string): Promise<RoleKeyRow[]> {
    return request<RoleKeyRow[]>(apiBase, token, '/api/v1/role-keys');
}

/** EAA-1817: `POST /api/v1/role-keys` — adds a key to the governed vocabulary. */
export function createRoleKey(
    apiBase: string,
    token: string,
    body: { key: string; name: string; description?: string; sort_order?: number }
): Promise<RoleKeyRow> {
    return request<RoleKeyRow>(apiBase, token, '/api/v1/role-keys', { method: 'POST', body });
}

/**
 * EAA-1817: `PATCH /api/v1/role-keys/{key}` — `key` itself is immutable (it is
 * the join key every Level and RoleMap row already carries; the body never
 * accepts it, only the path segment names which row).
 */
export function updateRoleKey(
    apiBase: string,
    token: string,
    key: string,
    body: { name?: string; description?: string; is_active?: boolean; sort_order?: number }
): Promise<RoleKeyRow> {
    return request<RoleKeyRow>(apiBase, token, `/api/v1/role-keys/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        body,
    });
}

/* ---- Role map restore / re-point / impact (EAA-1817) ---- */

/** EAA-1817: shared 6-field write-response shape of `restoreRoleMap` and `updateRoleMap`. */
export interface RoleMapWriteResult {
    id: string;
    role_key: string;
    application: string;
    app_role: string | null;
    organization_id: string | null;
    is_active: boolean;
}

/**
 * EAA-1817: `POST /api/v1/role-maps/{id}/restore` — undoes a soft delete.
 * Only works on an already soft-deleted row (`RoleMapService.restore` requires
 * `deleted_at__isnull=False`, 404 otherwise). There is NO server-side expiry —
 * any "10 second undo" window is purely a FRONTEND convention, not enforced here.
 */
export function restoreRoleMap(
    apiBase: string,
    token: string,
    id: string
): Promise<RoleMapWriteResult> {
    return request<RoleMapWriteResult>(apiBase, token, `/api/v1/role-maps/${id}/restore`, {
        method: 'POST',
    });
}

/**
 * EAA-1817: `PATCH /api/v1/role-maps/{id}` — the "re-point" write (swap
 * `app_role` and/or flip `is_active`); the body must supply at least one field.
 */
export function updateRoleMap(
    apiBase: string,
    token: string,
    id: string,
    body: { app_role?: string; is_active?: boolean }
): Promise<RoleMapWriteResult> {
    return request<RoleMapWriteResult>(apiBase, token, `/api/v1/role-maps/${id}`, {
        method: 'PATCH',
        body,
    });
}

export interface RoleMapImpact {
    id: string;
    role_key: string;
    application: string;
    organization_id: string | null;
    /** EAA-1817: DISTINCT user_sub count — this is the number to show in a confirm dialog. */
    account_count: number;
    /** EAA-1817: raw row count (always >= account_count) — NOT the confirm-dialog number. */
    assignment_count: number;
    remaining_app_roles: string[];
}

/** EAA-1817: `GET /api/v1/role-maps/{id}/impact` — blast radius before a destructive re-point/delete. */
export function fetchRoleMapImpact(
    apiBase: string,
    token: string,
    id: string
): Promise<RoleMapImpact> {
    return request<RoleMapImpact>(apiBase, token, `/api/v1/role-maps/${id}/impact`);
}

/* ---- Role map scope diff / override drop (EAA-1817) ---- */

export interface RoleMapScopeDiff {
    role_key: string;
    organization_id: string;
    has_override: boolean;
    default: string[];
    override: string[];
    added: string[];
    removed: string[];
    application: string;
}

/**
 * EAA-1817: `GET /api/v1/role-map/scope-diff` — compares an org's override
 * against the ecosystem default. An override is a wholesale REPLACEMENT set,
 * never merged with the default — `default`/`override` are computed as two
 * independent key sets server-side, and `added`/`removed` is their diff, not
 * an incremental edit log.
 */
export function fetchRoleMapScopeDiff(
    apiBase: string,
    token: string,
    params: { application: string; roleKey: string; organization: string }
): Promise<RoleMapScopeDiff> {
    const query = new URLSearchParams({
        application: params.application,
        role_key: params.roleKey,
        organization: params.organization,
    });
    return request<RoleMapScopeDiff>(
        apiBase,
        token,
        `/api/v1/role-map/scope-diff?${query.toString()}`
    );
}

/** EAA-1817: `DELETE /api/v1/role-map/override` — drops an org's override rows
 *  for this application/role_key, reverting resolution to the ecosystem default. */
export function deleteRoleMapOverride(
    apiBase: string,
    token: string,
    params: { application: string; roleKey: string; organization: string }
): Promise<{ role_key: string; application: string; organization_id: string; dropped: number }> {
    const query = new URLSearchParams({
        application: params.application,
        role_key: params.roleKey,
        organization: params.organization,
    });
    return request(apiBase, token, `/api/v1/role-map/override?${query.toString()}`, {
        method: 'DELETE',
    });
}

/* ---- App onboarding checklist / Keycloak client (EAA-1817) ---- */

export interface AppChecklist {
    code: string;
    catalog_synced: boolean;
    catalog_role_count: number;
    catalog_last_synced_at: string | null;
    catalog_last_sync_status: 'never' | 'ok' | 'failed';
    role_link_count: number;
    enabled: boolean;
    has_client: boolean;
    complete: boolean;
}

/** EAA-1817: `GET /api/v1/admin/apps/{code}/checklist` — the D11 onboarding readiness checklist. */
export function fetchAppChecklist(
    apiBase: string,
    token: string,
    code: string
): Promise<AppChecklist> {
    return request<AppChecklist>(
        apiBase,
        token,
        `/api/v1/admin/apps/${encodeURIComponent(code)}/checklist`
    );
}

/**
 * EAA-1709: for an `api`/`hybrid` app this now creates-or-rotates the MACHINE
 * client's (`<code>-api`) secret, and `client_id` is that `-api` id. A `web`
 * app's login client is public (PKCE) and has no secret, so the console never
 * offers this action for one.
 *
 * EAA-1817: `POST /api/v1/admin/apps/{code}/client` — creates or rotates the
 * app's Keycloak OIDC client. `client_secret` IS PLAINTEXT AND RETURNED IN
 * THIS RESPONSE ONLY — no read endpoint (registry, checklist, or otherwise)
 * can ever surface it again afterwards. The UI MUST reveal it exactly once
 * and never attempt to refetch it (same one-time-reveal contract as
 * `issueAccount`/`resetAccountPassword` below).
 */
export function createAppClient(
    apiBase: string,
    token: string,
    code: string
): Promise<{
    code: string;
    client_id: string;
    client_secret: string;
    rotated: boolean;
    warning: string;
    next_steps: AppChecklist;
}> {
    return request(apiBase, token, `/api/v1/admin/apps/${encodeURIComponent(code)}/client`, {
        method: 'POST',
    });
}

/**
 * EAA-1810: `POST /api/v1/admin/apps/{code}/external-system` — the HMAC half of
 * an external app's connection contract, and the sibling of `createAppClient`
 * above. That one hands the app an OIDC `client_id`/`client_secret` so a PERSON
 * can sign in; this one hands it a `system_code`/`shared_secret` so the app's
 * own BACKEND can call us with no user present. Most external apps need both.
 *
 * `shared_secret` IS PLAINTEXT AND RETURNED IN THIS RESPONSE ONLY — same
 * one-time-reveal contract as the client secret. Unlike that one it really is
 * stored server-side (verifying an inbound HMAC needs the plaintext on both
 * ends), but no read endpoint returns it, so a lost secret can only be rotated.
 *
 * `action` says which of three things happened, and the third is why this is not
 * a boolean:
 *
 *   - `created` — fresh credential, `shared_secret` present. Nothing was live.
 *   - `rotated` — the app was already linked, so this REPLACED a secret. Safe by
 *     construction (the previous one keeps verifying until
 *     `previous_secret_expires_at`) but the case the operator must act on: the
 *     app has to be redeployed before that window lapses.
 *   - `adopted` — an integration row with this code already existed and is now
 *     linked, WITHOUT being rotated, so **`shared_secret` is `null`**. That row
 *     may already be in live use by a partner holding the secret; rotating it as
 *     a side effect of "create" would disconnect them. A caller must therefore
 *     handle a null secret rather than assume a reveal is always available.
 */
export function provisionAppExternalSystem(
    apiBase: string,
    token: string,
    code: string
): Promise<{
    code: string;
    system_code: string;
    /** `null` for `action: 'adopted'` — see above. */
    shared_secret: string | null;
    action: 'created' | 'rotated' | 'adopted';
    rotated: boolean;
    previous_secret_expires_at: string | null;
    warning: string;
    external_system: AppExternalSystemLink;
}> {
    return request(
        apiBase,
        token,
        `/api/v1/admin/apps/${encodeURIComponent(code)}/external-system`,
        { method: 'POST' }
    );
}

/**
 * EAA-1810: `POST /api/v1/admin/integrations/{system_code}/confirm-rotation` —
 * the operator states, out of band, that the app is now using the NEW secret,
 * which ends the overlap window immediately instead of waiting for it to lapse.
 *
 * Addressed by `system_code`, not by app code: the row being confirmed lives in
 * the integrations registry, and this is the same endpoint the (not yet built)
 * integrations screen would call. Idempotent, and never returns a secret.
 */
export function confirmAppSecretRotation(
    apiBase: string,
    token: string,
    systemCode: string
): Promise<AppExternalSystemLink> {
    return request(
        apiBase,
        token,
        `/api/v1/admin/integrations/${encodeURIComponent(systemCode)}/confirm-rotation`,
        { method: 'POST' }
    );
}

export type ConnectionCheckProbeStatus = 'ok' | 'failed' | 'degraded' | 'skipped';

export interface ConnectionCheckProbe {
    id: string;
    status: ConnectionCheckProbeStatus;
    detail: string;
    remedy?: string;
}

export interface ConnectionCheckReport {
    checked_at: string;
    overall: ConnectionCheckProbeStatus;
    probes: ConnectionCheckProbe[];
}

/**
 * EAA-1846: `POST /api/v1/admin/apps/{code}/connection-check` (shipped under
 * EAA-1810's G3) — diagnoses an onboarded app's OIDC wiring (client still
 * exists in the realm, redirect URIs match the registry, an audience mapper
 * emits `app.audience`, the app's own `GET /roles` answers, back-channel
 * logout is registered and listening). Never returns a credential — see the
 * module doc on the server side.
 *
 * Server-side per-app cooldown (`BIZ_RATE_LIMIT_EXCEEDED`, `details.retry_after_seconds`)
 * means a caller must not fire this in a loop; it exists for an operator to
 * press once when diagnosing a specific failure, not for polling.
 */
export function checkAppConnection(
    apiBase: string,
    token: string,
    code: string
): Promise<ConnectionCheckReport> {
    return request(
        apiBase,
        token,
        `/api/v1/admin/apps/${encodeURIComponent(code)}/connection-check`,
        { method: 'POST' }
    );
}

/* ---- App permission groups (EAA-1846) ---- */

export interface AppPermissionGroup {
    key: string;
    name: string;
    description: string;
    permission_count: number;
    permissions: string[];
    is_system: boolean;
}

/**
 * EAA-1846: `GET /api/v1/admin/apps/{code}/permission-groups` — the app's
 * `TenantPermissionGroup` catalog (system groups + the acting org's own
 * custom groups). `available: false` is NOT an error response (still a 200):
 * it means this app/org combination has no ACH-side tenant scope to read a
 * catalog from, or ACH was unreachable — `reason` is a short human string
 * meant to be rendered as-is, not a code to branch on.
 */
export interface AppPermissionGroupsResponse {
    available: boolean;
    reason?: string;
    groups: AppPermissionGroup[];
}

export function fetchAppPermissionGroups(
    apiBase: string,
    token: string,
    code: string
): Promise<AppPermissionGroupsResponse> {
    return request<AppPermissionGroupsResponse>(
        apiBase,
        token,
        `/api/v1/admin/apps/${encodeURIComponent(code)}/permission-groups`
    );
}

/* ---- Org provisioning outcomes / retry / level preview (EAA-1817) ---- */

// EAA-1817 (frame 53, "Tài khoản" tab): `pending_revoke` was missing from this
// union — `provisioning.py`'s `PENDING_REVOKE = "pending_revoke"` is a real,
// distinct outcome (an account still holding a previous unit's access while
// the revoke has not gone out yet), never folded into `revoked`/`error`.
// Confirmed against the constants at the top of `directory/provisioning.py`.
export type ProvisioningOutcome =
    | 'pushed'
    | 'no_mapping'
    | 'paused_mapping'
    | 'ambiguous_mapping'
    | 'drift'
    | 'pending_revoke'
    | 'error'
    | 'revoked'
    | 'never_attempted';

/** EAA-1840 (WP-5, frame 53): the org a row's oldest queued `pending_revokes`
 *  entry was captured against — present only while the row still owes a
 *  revoke from a prior transfer. */
export interface ProvisioningTransferSourceOrg {
    id: string;
    name: string;
    code: string;
}

export interface ProvisioningOutcomeRow {
    assignment_id: string;
    user_sub: string;
    user_email: string;
    /** EAA-1840: joined from the cached realm snapshot — same field names
     *  `AccountDetail` already uses, so `accountDisplayName()` works unmodified. */
    first_name: string;
    last_name: string;
    /** EAA-1878: BOTH are null on a DIRECT grant — `outcomes_for_app`/`outcomes_for_org`
     *  read them off `row.level`, and the direct-grant path has no Level at all
     *  (`level` XOR `direct_application`, see the Assignment model). They were typed
     *  `string`, which is what let a `role_key.startsWith(...)` reach production and
     *  white-screen the accounts tab on a response where every row was a direct
     *  grant. Nullable here so the compiler forces the check at every use. */
    level: string | null;
    role_key: string | null;
    application: string;
    is_active: boolean;
    outcome: ProvisioningOutcome;
    outcome_at: string;
    detail: string;
    fix: string;
    sync_status: string;
    sync_attempts: number;
    transfer_from_organization: ProvisioningTransferSourceOrg | null;
}

export interface ProvisioningOutcomes {
    counts: Record<ProvisioningOutcome, number>;
    needs_attention: number;
    rows: ProvisioningOutcomeRow[];
}

/**
 * EAA-1817: `GET /api/v1/organizations/{orgId}/provisioning-outcomes` — this
 * is NOT paginated server-side, despite the SRS claiming it is:
 * `AssignmentService.outcomes_for_org` returns the full row list in one
 * response with no `.count()`/slicing applied, so callers must window/paginate
 * this CLIENT-side.
 */
export function fetchProvisioningOutcomes(
    apiBase: string,
    token: string,
    orgId: string
): Promise<ProvisioningOutcomes> {
    return request<ProvisioningOutcomes>(
        apiBase,
        token,
        `/api/v1/organizations/${encodeURIComponent(orgId)}/provisioning-outcomes`,
        { orgId }
    );
}

/** EAA-1888: one row of `GET /admin/apps/{code}/accounts` — same shape as
 *  `ProvisioningOutcomeRow` plus the owning `organization`, which that
 *  per-org endpoint leaves implicit (the caller already knows which org it
 *  asked for; this cross-org endpoint does not). */
export interface AppAccountRow extends ProvisioningOutcomeRow {
    organization: ProvisioningTransferSourceOrg;
    /** EAA-1878: the Keycloak login name, joined per page from the realm
     *  snapshot — the same join `first_name`/`last_name` come from. Declared HERE
     *  rather than on `ProvisioningOutcomeRow` because only this cross-org
     *  endpoint returns it; the per-org `provisioning-outcomes` endpoint does
     *  not, and widening the shared row would advertise a field that arrives
     *  undefined there. Empty when Keycloak has no user for `user_sub` (an
     *  identity that was deleted, or a stale `external_sub` the source app still
     *  reports) — the caller falls back rather than rendering a blank name. */
    username: string;
}

export interface AppAccountsOrgFacet {
    id: string;
    name: string;
    code: string;
    count: number;
}

export interface AppAccountsRoleFacet {
    role_key: string | null;
    count: number;
}

/** EAA-1888: counts over the app-relevant, visibility-scoped set — NEVER
 *  narrowed by whichever `outcome`/`organization_id`/`role_key`/`search`
 *  filter is currently applied, so the "Tài khoản" tab's filter chips and
 *  dropdowns keep showing "how many rows have this value in total". */
export interface AppAccountsFacets {
    outcome_counts: Partial<Record<ProvisioningOutcome, number>>;
    needs_attention: number;
    organizations: AppAccountsOrgFacet[];
    roles: AppAccountsRoleFacet[];
}

export interface AppAccountsResult {
    results: AppAccountRow[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    facets: AppAccountsFacets;
}

export interface AppAccountsParams {
    page?: number;
    pageSize?: number;
    search?: string;
    outcome?: ProvisioningOutcome;
    organizationId?: string;
    roleKey?: string;
    ordering?: string;
}

/**
 * EAA-1888: `GET /admin/apps/{code}/accounts` — the cross-org, genuinely
 * paginated/filtered/searched replacement for the console "Tài khoản" tab's
 * own per-organization fan-out (fetching EVERY org's full
 * `fetchProvisioningOutcomes` result and merging client-side). Every param is
 * applied server-side; `facets` in the response is the only thing computed
 * over the UNFILTERED app-relevant scope (see `AppAccountsFacets`).
 */
export function fetchAppAccounts(
    apiBase: string,
    token: string,
    code: string,
    params?: AppAccountsParams
): Promise<AppAccountsResult> {
    const query = new URLSearchParams({
        page: String(params?.page ?? 1),
        page_size: String(params?.pageSize ?? 8),
    });
    if (params?.search) query.set('search', params.search);
    if (params?.outcome) query.set('outcome', params.outcome);
    if (params?.organizationId) query.set('organization_id', params.organizationId);
    if (params?.roleKey) query.set('role_key', params.roleKey);
    if (params?.ordering) query.set('ordering', params.ordering);
    return request<AppAccountsResult>(
        apiBase,
        token,
        `/api/v1/admin/apps/${code}/accounts?${query.toString()}`
    );
}

/**
 * EAA-1817: `POST /api/v1/organizations/{orgId}/provisioning-retry` —
 * requeues provisioning for the given `assignment_ids` and/or every row
 * matching `outcome`; the body must supply at least one of the two.
 */
export function retryProvisioning(
    apiBase: string,
    token: string,
    orgId: string,
    body: { assignment_ids?: string[]; outcome?: string }
): Promise<{ requeued: number; assignment_ids: string[] }> {
    return request(
        apiBase,
        token,
        `/api/v1/organizations/${encodeURIComponent(orgId)}/provisioning-retry`,
        { method: 'POST', body, orgId }
    );
}

export interface LevelPreviewApplication {
    application: string;
    scope_id: string;
    state: 'linked' | 'paused' | 'none';
    app_roles: string[];
    reason: string;
}

export interface LevelPreview {
    role_key: string;
    applications: LevelPreviewApplication[];
    grants_something: boolean;
    warning: string;
}

/**
 * EAA-1817: `GET /api/v1/organizations/{orgId}/levels/preview?role_key=` —
 * dry-run of what a `role_key` would grant if assigned in this org, BEFORE a
 * Level authoring it is actually created.
 */
export function fetchLevelPreview(
    apiBase: string,
    token: string,
    orgId: string,
    roleKey: string
): Promise<LevelPreview> {
    const query = new URLSearchParams({ role_key: roleKey });
    return request<LevelPreview>(
        apiBase,
        token,
        `/api/v1/organizations/${encodeURIComponent(orgId)}/levels/preview?${query.toString()}`,
        { orgId }
    );
}

/* ---- Accounts (D12 identity writes) ---- */

export function issueAccount(
    apiBase: string,
    token: string,
    body: {
        username: string;
        email?: string;
        first_name?: string;
        last_name?: string;
        credential_mode: 'invite' | 'temp_password';
    }
): Promise<AccountIssued> {
    return request<AccountIssued>(apiBase, token, '/api/v1/accounts', { method: 'POST', body });
}

export function deleteAccount(apiBase: string, token: string, sub: string): Promise<void> {
    return request<void>(apiBase, token, `/api/v1/accounts/${encodeURIComponent(sub)}`, {
        method: 'DELETE',
    });
}

// EAA-1820 (frame 86, "Vô hiệu hoá tài khoản"): `AccountDetailView.patch` is a
// plain APIView with no serializer at all — it reads `request.data['enabled']`
// directly and 400s unless it is exactly a bool. Body is `{ enabled }` plus an
// optional `end_sessions` when locking (EAA-1820, frames 75/80) to revoke open
// Keycloak sessions via Admin API logout — distinct from disable alone, which
// only blocks new logins. There is no field to persist a "Lý do" (reason) or a
// hand-off note, which the design's frame 86 dialog shows. Those two fields are
// NOT rendered by the dialog this call backs — inventing a request body the
// server silently drops would be worse than omitting the field. `enabled: false`
// disables login; `enabled: true` re-enables — nothing here is destroyed, so
// this is the one identity write in this file that is genuinely reversible.
export function updateAccountEnabled(
    apiBase: string,
    token: string,
    sub: string,
    enabled: boolean,
    options?: { endSessions?: boolean }
): Promise<{ sub: string; enabled: boolean; sessions_ended?: boolean }> {
    const body: { enabled: boolean; end_sessions?: boolean } = { enabled };
    if (!enabled && options?.endSessions) {
        body.end_sessions = true;
    }
    return request<{ sub: string; enabled: boolean; sessions_ended?: boolean }>(
        apiBase,
        token,
        `/api/v1/accounts/${encodeURIComponent(sub)}`,
        { method: 'PATCH', body }
    );
}

/**
 * EAA-1781: super-admin password reset by username OR sub.
 *
 * EAA-1779: the credential mode is derived from the body, not named in it —
 * omit `new_password` and the server generates a temporary one (+ forced change
 * at next login, the original behaviour); supply it and that exact value is set,
 * permanently unless `force_change` is true. The supplied password is NEVER
 * returned in the response, so the caller must keep whatever it sent.
 */
export function resetAccountPassword(
    apiBase: string,
    token: string,
    body: {
        username?: string;
        sub?: string;
        end_sessions?: boolean;
        new_password?: string;
        force_change?: boolean;
    }
): Promise<AccountPasswordReset> {
    return request<AccountPasswordReset>(apiBase, token, '/api/v1/accounts/reset-password', {
        method: 'POST',
        body,
    });
}

/** EAA-1779: one row of `POST /accounts/bulk-reset-password`. */
export interface AccountBulkPasswordResetRow {
    sub: string;
    username: string;
    ok: boolean;
    /** Per-row one-time temp password in `temp_password` mode; `null` in `admin_set`. */
    temp_password: string | null;
    sessions_ended: boolean;
    error: string | null;
}

export interface AccountBulkPasswordReset {
    mode: AccountResetMode;
    force_change: boolean;
    ok_count: number;
    failed_count: number;
    results: AccountBulkPasswordResetRow[];
}

/**
 * EAA-1779: reset many accounts with ONE credential body in ONE request.
 *
 * Replaces the console's per-row loop over `resetAccountPassword` — 100 selected
 * rows used to mean 100 concurrent requests, each holding a database connection on
 * the server. A failed row comes back as `ok: false` with `error`; the request itself
 * only fails when nothing could run (validation, realm not configured, auth).
 */
export function bulkResetAccountPasswords(
    apiBase: string,
    token: string,
    body: {
        subs: string[];
        end_sessions?: boolean;
        new_password?: string;
        force_change?: boolean;
    }
): Promise<AccountBulkPasswordReset> {
    return request<AccountBulkPasswordReset>(apiBase, token, '/api/v1/accounts/bulk-reset-password', {
        method: 'POST',
        body,
    });
}

/* ---- Audit ---- */

/** EAA-1821: shared query surface for both audit endpoints. Multi-value fields
 *  are serialised comma-separated; the backend splits on comma. */
export interface AuditQueryParams {
    page?: number;
    page_size?: number;
    /** Multi-select. Empty/omitted means "all". */
    action?: string[];
    /** '' | 'true' | 'false' */
    success?: string;
    /** Free-text across the row's searchable columns. */
    q?: string;
    /** Per-column search on "Người thao tác". */
    actor?: string;
    /** ISO-8601 date (YYYY-MM-DD) or datetime, inclusive lower bound on created_at. */
    from?: string;
    /** ISO-8601 date (YYYY-MM-DD) or datetime, inclusive upper bound on created_at. */
    to?: string;
    /** Whitelisted: 'created_at' | '-created_at' | 'action' | '-action' | 'success' | '-success' */
    ordering?: string;
}

export interface AccountAuditQueryParams extends AuditQueryParams {
    /** Per-column search on "Đối tượng". */
    username?: string;
    /** Multi-select on "Chế độ" (credential_mode). */
    credential_mode?: string[];
}

export interface AdminAuditQueryParams extends AuditQueryParams {
    /** Multi-select on "Đối tượng" category. */
    target_type?: string[];
    /** Per-column search on the target id. */
    target_id?: string;
}

/**
 * EAA-1821: the audit screen grew a rich filter bar (date range, free-text search,
 * per-column actor/target search, multi-select action & mode, sortable columns) and
 * the old single-value param objects could not express any of it — one `action`
 * string cannot say "create OR delete", and there was nowhere to put a date range
 * at all. This helper owns the fields both endpoints share so the two fetchers stay
 * a single source of truth and each only adds its own extras.
 *
 * Multi-value fields are joined with `,` because the backend splits on comma;
 * empty strings, empty arrays and `undefined` are skipped so an untouched filter
 * never narrows the result set.
 */
function appendAuditParams(query: URLSearchParams, params: AuditQueryParams): void {
    if (params.page && params.page > 1) query.set('page', String(params.page));
    if (params.action && params.action.length > 0) query.set('action', params.action.join(','));
    if (params.success) query.set('success', params.success);
    if (params.q) query.set('q', params.q);
    if (params.actor) query.set('actor', params.actor);
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.ordering) query.set('ordering', params.ordering);
}

export function fetchAccountAudit(
    apiBase: string,
    token: string,
    params: AccountAuditQueryParams
): Promise<Paginated<AuditRow>> {
    // EAA-1821: `page_size` is now honoured here too. It only ever worked on the admin
    // fetcher, so a caller asking this one for a short "recent activity" page silently
    // got 20 rows — an asymmetry with no reason behind it, closed rather than mirrored.
    const query = new URLSearchParams({ page_size: String(params.page_size ?? 20) });
    appendAuditParams(query, params);
    if (params.username) query.set('username', params.username);
    if (params.credential_mode && params.credential_mode.length > 0) {
        query.set('credential_mode', params.credential_mode.join(','));
    }
    return request<Paginated<AuditRow>>(
        apiBase,
        token,
        `/api/v1/accounts/audit?${query.toString()}`
    );
}

/** EAA-1814: GET /api/v1/admin/audit — the non-identity admin-write audit.
 *  EAA-1817: gated on `AUDIT_VIEW` (`@require_permissions`), not `IsSuperAdmin`
 *  as this line used to say — see the file header on that wholesale correction. */
export function fetchAdminAudit(
    apiBase: string,
    token: string,
    params: AdminAuditQueryParams
): Promise<Paginated<AdminAuditRow>> {
    const query = new URLSearchParams({ page_size: String(params.page_size ?? 20) });
    appendAuditParams(query, params);
    if (params.target_type && params.target_type.length > 0) {
        query.set('target_type', params.target_type.join(','));
    }
    if (params.target_id) query.set('target_id', params.target_id);
    return request<Paginated<AdminAuditRow>>(
        apiBase,
        token,
        `/api/v1/admin/audit?${query.toString()}`
    );
}

/* ---- Runtime config (Cấu hình) ---- */

/**
 * EAA-1823: `GET /api/v1/admin/config` returns the effective config as a flat
 * key -> value map, built by iterating the server's `READABLE_KEYS` allowlist.
 *
 * Deliberately typed as `unknown` values rather than a hand-written interface of
 * the current 22 keys: the key set is a server-side allowlist that grows, and a
 * mirrored interface here would silently hide every key added after it was
 * written. Callers narrow per value; `EDITABLE_SPEC_KINDS` below is the one
 * place that says what a given key's editor looks like.
 */
export type EffectiveConfig = Record<string, unknown>;

export interface StagedConfigChange {
    id: string;
    key: string;
    new_value: unknown;
    status: 'pending' | 'confirmed' | 'expired';
    staged_by: string;
    confirmed_by: string;
    expires_at: string | null;
    confirmed_at: string | null;
    created_at: string;
}

/**
 * The stage response, plus the one-time confirmation token.
 *
 * `token` is revealed EXACTLY once, in this response, and only its hash is
 * persisted server-side. Hold it in component state between stage and confirm
 * and treat it as a secret: never log it, never put it in a query key, never
 * persist it to storage or the URL.
 */
export interface StagedConfigChangeWithToken extends StagedConfigChange {
    token: string;
}

export function fetchEffectiveConfig(apiBase: string, token: string): Promise<EffectiveConfig> {
    return request<EffectiveConfig>(apiBase, token, '/api/v1/admin/config');
}

/**
 * Step 1 of 2. `value` is sent as its real JSON type — the server type-checks it
 * against `EDITABLE_SPEC` and rejects `"7"` where it wants `7`, so callers must
 * parse input before staging rather than passing raw strings from a text field.
 */
export function stageConfigChange(
    apiBase: string,
    token: string,
    key: string,
    value: unknown
): Promise<StagedConfigChangeWithToken> {
    return request<StagedConfigChangeWithToken>(apiBase, token, '/api/v1/admin/config/stage', {
        method: 'POST',
        body: { key, value },
    });
}

/** Step 2 of 2. `confirmToken` is the one-time token from `stageConfigChange`. */
export function confirmConfigChange(
    apiBase: string,
    token: string,
    changeId: string,
    confirmToken: string
): Promise<StagedConfigChange> {
    return request<StagedConfigChange>(
        apiBase,
        token,
        `/api/v1/admin/config/${encodeURIComponent(changeId)}/confirm`,
        { method: 'POST', body: { token: confirmToken } }
    );
}

/**
 * EAA-1823: one editable key's declared type and bounds, as served by
 * `GET /api/v1/admin/config/editable`. Mirrors the server's `EDITABLE_SPEC`
 * entry shape; `type` is a stable wire name, not a Python repr.
 */
export interface EditableConfigSpec {
    type: 'int' | 'bool' | 'str' | 'list';
    min?: number;
    max?: number;
    max_length?: number;
    format?: 'url';
    item_format?: 'origin';
    max_items?: number;
}

export type EditableConfigSpecMap = Record<string, EditableConfigSpec>;

/**
 * Which config keys may be edited, and within what bounds.
 *
 * Fetched rather than hardcoded on purpose: a copy of this table in the client
 * drifts the moment the server adds a key — a newly editable key would keep
 * rendering read-only, or an editor would offer a range the server rejects.
 */
export function fetchEditableConfigSpec(
    apiBase: string,
    token: string
): Promise<EditableConfigSpecMap> {
    return request<EditableConfigSpecMap>(apiBase, token, '/api/v1/admin/config/editable');
}

/**
 * EAA-1823: per-key provenance from `GET /api/v1/admin/config/overrides`.
 *
 * `default` is the value the service would use with NO override — read from
 * settings, so it stays the "changed from" reference even after an override is
 * applied. Served apart from the effective config because that endpoint's flat
 * `{KEY: value}` body cannot carry per-key metadata without breaking its shape.
 */
export interface ConfigOverrideMeta {
    default: unknown;
    overridden: boolean;
    updated_by: string;
    updated_at: string | null;
}

export type ConfigOverrideMetaMap = Record<string, ConfigOverrideMeta>;

export function fetchConfigOverrides(
    apiBase: string,
    token: string
): Promise<ConfigOverrideMetaMap> {
    return request<ConfigOverrideMetaMap>(apiBase, token, '/api/v1/admin/config/overrides');
}

/* ---- Per-app runtime config (EAA-1823: tab "Cấu hình" của /console/apps/:code) ---- */

/**
 * EAA-1823: where a key's effective value came from.
 *
 * Three values, not a boolean: portal-api's own `/admin/config/overrides` says
 * only `overridden: true|false`, which cannot tell "shipped default" apart from
 * "set by the environment" — and those have different cures. An operator who
 * sees `env` edits the deployment; one who sees `override` edits it here.
 */
export type AppConfigSource = 'default' | 'env' | 'override';

/**
 * EAA-1823: one config key as a service reports it, per the contract in
 * `_handoff/STATUS.md` §"Phụ lục B-CONTRACT".
 *
 * Deliberately ONE object per key rather than the three parallel maps
 * portal-api serves (`/admin/config` + `/editable` + `/overrides`). That split
 * exists because a flat `{KEY: value}` body cannot carry per-key metadata (see
 * `EffectiveConfig` above); a service designing this endpoint fresh has no
 * reason to inherit the constraint.
 *
 * `label`/`description` come from the SERVICE, unlike `/console/config` which
 * reads them from the FE locale files (`Config/configDisplay.ts`). The portal
 * cannot ship Vietnamese labels for the keys of an arbitrary service — including
 * services that do not exist yet — so the service that owns the key owns its
 * copy. Missing label falls back to the raw key.
 */
export interface AppConfigKey {
    key: string;
    /** Effective value. `null` when `is_secret` — never a masked or truncated secret. */
    value: unknown;
    /** What the service would use with no override; the "changed from" reference. */
    default: unknown;
    source: AppConfigSource;
    editable: boolean;
    requires_restart: boolean;
    is_secret: boolean;
    /**
     * Whether a secret actually has a value. PENDING: gated behind backlog item I
     * (widening a security surface's disclosure is its own reviewed change, not a
     * side effect of this tab). Treat as possibly absent until that lands.
     */
    is_set?: boolean;
    /** Service-supplied display name; falls back to `key`. */
    label?: string;
    description?: string;
    /** Type and bounds for the editor. `null` when not editable. Write ships later. */
    spec: EditableConfigSpec | null;
    updated_by?: string;
    updated_at?: string | null;
}

/**
 * `supported: false` is a 200, not a 404: "this service does not expose config"
 * is an answer, and it must not be confused with "the call failed". The two get
 * different UI and different operator action.
 */
export interface AppConfigResponse {
    app_code: string;
    supported: boolean;
    generated_at: string;
    keys: AppConfigKey[];
}

/**
 * TODO(EAA-1823 / gói H): `GET /api/v1/apps/{code}/config` DOES NOT EXIST YET.
 * portal-api serves config for itself only (`portal_api/urls.py:434-443`); there
 * is no per-app route. The proxy is being built to the contract above. Until it
 * lands this call 404s and the tab renders its error state — which is correct,
 * and is why no sample data is faked here.
 */
export function fetchAppConfig(
    apiBase: string,
    token: string,
    code: string
): Promise<AppConfigResponse> {
    return request<AppConfigResponse>(
        apiBase,
        token,
        `/api/v1/apps/${encodeURIComponent(code)}/config`
    );
}

/* ---- Storage spaces (Kho lưu trữ) ---- */

/**
 * ECR-85: one storage space as portal-api's `admin/storage/spaces` proxy returns
 * it — exactly these fields (the proxy reduces Storage's row; see
 * `portal_api/directory/storage_views.py`). The console never calls storage-api:
 * the cross-org list and the quota write both need `storage.platform.admin`, which
 * no person can hold, so portal-api calls Storage on its machine client.
 *
 * `quota_bytes` is the EFFECTIVE quota — the app's policy default when
 * `quota_override_bytes` is null, the override otherwise.
 */
export interface StorageSpace {
    id: string;
    organization_id: string;
    organization_name: string;
    app_code: string;
    scope_id: string;
    quota_bytes: number;
    quota_override_bytes: number | null;
    used_bytes: number;
    reserved_bytes: number;
    available_bytes: number;
    file_count: number;
    status: string;
    accepts_writes: boolean;
}

export interface StorageSpaceQuery {
    page?: number;
    pageSize?: number;
    search?: string;
    app?: string;
}

/** `GET /api/v1/admin/storage/spaces` — `console.storage.view`. */
export function fetchStorageSpaces(
    apiBase: string,
    token: string,
    query: StorageSpaceQuery = {}
): Promise<Paginated<StorageSpace>> {
    const qs = new URLSearchParams();
    if (query.page) qs.set('page', String(query.page));
    if (query.pageSize) qs.set('page_size', String(query.pageSize));
    if (query.search?.trim()) qs.set('search', query.search.trim());
    if (query.app?.trim()) qs.set('app', query.app.trim());
    const suffix = qs.toString();
    return request<Paginated<StorageSpace>>(
        apiBase,
        token,
        `/api/v1/admin/storage/spaces${suffix ? `?${suffix}` : ''}`
    );
}

/**
 * `PATCH /api/v1/admin/storage/spaces/{id}` — `console.storage.manage`.
 *
 * ECR-85: the body is exactly `{quota_override_bytes}`; `null` clears the override
 * so the space falls back to its app's default, and is SENT (not omitted) because
 * clearing is the statement being made.
 */
export function updateStorageSpaceQuota(
    apiBase: string,
    token: string,
    spaceId: string,
    quotaOverrideBytes: number | null
): Promise<StorageSpace> {
    return request<StorageSpace>(
        apiBase,
        token,
        `/api/v1/admin/storage/spaces/${encodeURIComponent(spaceId)}`,
        { method: 'PATCH', body: { quota_override_bytes: quotaOverrideBytes } }
    );
}
